import React, { useEffect, useMemo, useRef, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { API_URL } from '../constants';
import AlertModal from './AlertModal';
import '../../../../styles/marketer/project/DescriptionModal.css';

const FORMATS = ['size', 'bold', 'italic', 'underline', 'color', 'list'];

// Quill Size 포맷 등록
const Size = Quill.import('attributors/style/size');
Size.whitelist = ['12px', '14px', '16px', '18px', '20px', '22px', '24px'];
Quill.register(Size, true);

export default function DescriptionModal({ open, onClose, projectId, initialDescription, onSaved, onSave }) {
  const holderRef = useRef(null);
  const quillRef = useRef(null);
  const counterTextRef = useRef(null); // 실시간 숫자 표시를 위한 직접 참조
  const [len, setLen] = useState(0);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const modules = useMemo(() => {
    const Keyboard = Quill.import('modules/keyboard');
    
    return {
      toolbar: [
        [{ size: ['12px', '14px', '16px', '18px', '20px', '22px', '24px'] }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean']
      ],
      clipboard: { matchVisual: false },
      keyboard: {
        bindings: {
          ...Keyboard.DEFAULTS.bindings,
          handleEnter: {
            key: 'Enter',
            handler: function(range, context) {
              // 현재 포맷 가져오기
              const formats = this.quill.getFormat(range.index);
              
              // 줄바꿈 삽입
              this.quill.insertText(range.index, '\n', Quill.sources.USER);
              
              // 새 줄로 커서 이동
              this.quill.setSelection(range.index + 1, Quill.sources.SILENT);
              
              // 포맷 적용 (size가 없으면 16px 기본값)
              const newFormats = { ...formats };
              if (!newFormats.size) {
                newFormats.size = '16px';
              }
              
              Object.keys(newFormats).forEach(key => {
                this.quill.format(key, newFormats[key], Quill.sources.USER);
              });
              
              return false;
            }
          }
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!open || !holderRef.current) return;

    // 1. 에디터 초기화
    holderRef.current.innerHTML = '';
    const q = new Quill(holderRef.current, {
      theme: 'snow',
      modules,
      formats: FORMATS,
      placeholder: '프로젝트 내용을 상세하게 작성해주세요...'
    });
    quillRef.current = q;

    // 2. 데이터 주입
    if (initialDescription) {
      q.clipboard.dangerouslyPasteHTML(initialDescription);
    } else {
      // 빈 에디터일 때 기본 글자 크기 설정
      q.format('size', '16px');
    }

    // 3. 통합 글자 수 업데이트 함수 (Native DOM 기반)
    const handleUpdate = () => {
      if (!q || !q.root) return;
      
      // 한글 조합 중에도 즉시 반영되도록 root.innerText를 직접 사용합니다.
      const domText = q.root.innerText || "";
      const actualLen = domText.replace(/\n$/, "").length;
      
      // A. 리액트 상태 업데이트 (저장 버튼 활성화 로직용)
      setLen(actualLen);
      
      // B. DOM 직접 업데이트 (한글 입력 시 지연 없는 실시간 표시용)
      if (counterTextRef.current) {
        counterTextRef.current.textContent = `${actualLen} / 2000`;
        
        if (actualLen > 2000) {
          counterTextRef.current.className = 'description-modal__counter-text description-modal__counter-text--error';
        } else if (actualLen > 0) {
          counterTextRef.current.className = 'description-modal__counter-text description-modal__counter-text--ok';
        } else {
          counterTextRef.current.className = 'description-modal__counter-text';
        }
      }
    };

    // 4. 모든 종류의 입력 이벤트 감시 (한글 IME 대응 강화)
    q.on('text-change', handleUpdate);
    q.root.addEventListener('input', handleUpdate);
    q.root.addEventListener('compositionupdate', handleUpdate);
    q.root.addEventListener('keyup', handleUpdate);

    // 5. 초기 설정 및 포커스
    handleUpdate();
    setTimeout(() => {
      if (q) {
        q.focus();
        const length = q.getLength();
        q.setSelection(length, 0);
      }
    }, 100);

    return () => {
      if (q && q.root) {
        q.off('text-change', handleUpdate);
        q.root.removeEventListener('input', handleUpdate);
        q.root.removeEventListener('compositionupdate', handleUpdate);
        q.root.removeEventListener('keyup', handleUpdate);
      }
      quillRef.current = null;
    };
  }, [open, initialDescription, modules]);

  const ok = len > 0 && len <= 2000;

  const save = async () => {
    if (!ok) return setAlertMessage('2000자 이내로 입력해주세요.');
    const html = quillRef.current?.root?.innerHTML || '';

    // onSave prop이 있으면 내부 API 호출 없이 콜백만 실행 (관리자 모드)
    if (onSave) {
      onSave(html);
      onClose();
      return;
    }

    // 기존 광고주 플로우 유지
    if (!projectId) return setAlertMessage('먼저 1단계를 저장해주세요.');
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/marketer/mybiz/projects/${encodeURIComponent(projectId)}/description`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: html })
      });
      const j = await res.json();
      if (!res.ok || !j?.ok) throw new Error(j?.message || '저장 실패');

      onSaved(j.description || html);
      onClose();
    } catch (e) {
      setAlertMessage(e.message || '저장 실패');
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="description-modal mbm-overlay">
      <div className="description-modal__content mbm">
        <button onClick={onClose} className="description-modal__close mbm-close">×</button>

        <div className="description-modal__header">
          <div className="description-modal__icon">📝</div>
          <div className="description-modal__title mbm-title">프로젝트 설명 및 요청사항 입력</div>
        </div>

        <div className="description-modal__editor-container">
          <div ref={holderRef} className="description-modal__editor" />
          <div className="description-modal__counter">
            <div className="description-modal__counter-label">
              {len > 2000 ? (
                <span className="description-modal__counter-error">⚠️ 글자 수 초과</span>
              ) : (
                <span className="description-modal__counter-hint">
                  {len > 0 ? '✓ 작성 중' : '💡 최소 1자 이상 입력해주세요'}
                </span>
              )}
            </div>
            <div 
              ref={counterTextRef}
              className={`description-modal__counter-text ${len > 2000 ? 'description-modal__counter-text--error' : len > 0 ? 'description-modal__counter-text--ok' : ''}`}
            >
              {len} / 2000
            </div>
          </div>
        </div>

        <div className="description-modal__actions mbm-foot">
          <button type="button" onClick={save} disabled={!ok || saving} className="description-modal__button description-modal__button--submit mb-btn mb-btn-primary">
            {saving ? '저장 중...' : '확 인'}
          </button>
        </div>
      </div>

      <AlertModal 
        open={!!alertMessage} 
        onClose={() => setAlertMessage('')} 
        message={alertMessage} 
      />
    </div>
  );
}
